import { Navigate, useSearchParams } from 'react-router-dom';

import { useMe, usePartnersReadMe } from '@/api/hooks';
import LoadErrorState from '@/components/LoadErrorState';
import { canEditPartnerAccount } from '@/domain/account-status/utils';
import OnboardingForm from '@/domain/onboarding/components/OnboardingForm';
import OnboardingSkeleton from '@/domain/onboarding/components/OnboardingSkeleton';
import { type OnboardingMode } from '@/domain/onboarding/types';
import { emptyOnboardingValues, fromPartner } from '@/domain/onboarding/utils';
import { useTranslate } from '@/state/i18n';
import { ScreenRoutes } from '@/types/router';

/**
 * `/onboarding` creates the partner account; `/onboarding?mode=edit` edits it while it is in review
 * or after a rejection. Anything else goes back to `/`, where the account gate decides.
 */
const Onboarding = () => {
  const t = useTranslate('domain.onboarding.Onboarding');
  const [searchParams] = useSearchParams();
  const mode: OnboardingMode = searchParams.get('mode') === 'edit' ? 'edit' : 'create';
  const { data: account, isLoading, isError, refetch } = usePartnersReadMe();
  const { data: user } = useMe();

  if (isLoading) return <OnboardingSkeleton />;
  if (isError) return <LoadErrorState message={t('loadError')} onRetry={() => refetch()} />;

  if (mode === 'create' && account?.partner) return <Navigate to={ScreenRoutes.HOME} replace />;
  if (mode === 'edit' && !canEditPartnerAccount(account)) {
    return <Navigate to={ScreenRoutes.HOME} replace />;
  }

  return (
    <OnboardingForm
      mode={mode}
      defaultValues={
        mode === 'edit' && account?.partner
          ? fromPartner(account.partner)
          : emptyOnboardingValues(user)
      }
    />
  );
};

export default Onboarding;
